<script lang="ts" setup>
import { defineAsyncComponent, watch, watchPostEffect } from 'vue';
import { formatLabel, useTrackingStore, type VehicleDataContext } from '@dh-io-owpi/shared/src/stores/tracking';
import { provideI18nFromAEM } from '@dh-io-owpi/shared/src/plugins/i18n/aem';
import { initializeApi } from '@dh-io-owpi/backend-api';
import { getConfiguration } from '@dh-io-owpi/shared/src/lib/configuration';

// components
import SPASection from './components/SPASection.vue';
import SubNavigation from './components/SubNavigation/SubNavigation.vue';
// sections
import SectionStage from './sections/stage/SectionStage.vue';
import SectionUsp from './sections/usp/SectionUsp.vue';
const SectionExterior = defineAsyncComponent(() => import('./sections/exterior/SectionExterior.vue'));
const SectionInterior = defineAsyncComponent(() => import('./sections/interior/SectionInterior.vue'));
const SectionNba = defineAsyncComponent(() => import('./sections/nba/SectionNba.vue'));
const SectionFeatures = defineAsyncComponent(() => import('./sections/features/SectionFeatures.vue'));
const SectionTechData = defineAsyncComponent(() => import('./sections/tech-data/SectionTechData.vue'));
const SectionOfferTeaser = defineAsyncComponent(() => import('./sections/offer-teaser/SectionOfferTeaser.vue'));
// skeletons
import SectionExteriorSkeleton from './sections/exterior/SectionExteriorSkeleton.vue';
import SectionInteriorSkeleton from './sections/interior/SectionInteriorSkeleton.vue';
import SectionNbaSkeleton from './sections/nba/SectionNbaSkeleton.vue';
import SectionFeaturesSkeleton from './sections/features/SectionFeaturesSkeleton.vue';
import SectionTechDataSkeleton from './sections/tech-data/SectionTechDataSkeleton.vue';
import SectionOfferTeaserSkeleton from './sections/offer-teaser/SectionOfferTeaserSkeleton.vue';
// composables
import { useAemData } from './composables/useAemData';
// stores
import { useTheme } from './stores/theme';
import { useVehicleStore } from './stores/vehicle';
import { anchorSectionMap, useSectionsStore } from './stores/sections';
import { useStageSwitchersStore } from './stores/stageSwitchers';

const aemData = useAemData();

initializeApi(getConfiguration(aemData.stage).apiBaseUrl);

provideI18nFromAEM(aemData);

const theme = useTheme();
const stageSwitcherStore = useStageSwitchersStore();
const vehicleStore = useVehicleStore();
const sectionsStore = useSectionsStore();
const tracking = useTrackingStore();
tracking.setAppId('pi_spa');

watch(
  () => vehicleStore.stage,
  (stage, oldStage) => {
    // only send tracking on style change
    if (!stage || stage.currentSelection.styleId == oldStage?.currentSelection.styleId) return;

    const vehicle: VehicleDataContext = {
      subBrand: stageSwitcherStore.brandId.value,
      variant: formatLabel(stageSwitcherStore.selectedStyle.value?.name),
    };

    tracking.pageview({
      // hardcoded for now as we only have one page for the spa, we could use extractPageName(window.location.pathname) to get the page name
      name: 'overview',
      type: 'generic-product-template',
      vehicle,
    });

    tracking.setVehicleData(vehicle);
  },
);
// reset sections store on sub-brand change to ensure we try to fetch data again for those sections
watch(stageSwitcherStore.brandId, () => {
  sectionsStore.reset();
});

const cleanup = watchPostEffect(() => {
  if (vehicleStore.stage) {
    const section = anchorSectionMap[window.location.hash.slice(1)];
    if (section) sectionsStore.scrollIntoView(section);
    cleanup();
  }
});
</script>

<template>
  <main :class="['owpi-spa', `owpi-spa--theme-${theme.wb}`]">
    <SubNavigation :isDark="theme.dark" />
    <slot />
    <SPASection name="stage">
      <SectionStage />
    </SPASection>
    <SPASection name="usp" :label="$t('usp.subline')">
      <SectionUsp />
    </SPASection>

    <SPASection
      name="exterior"
      loading="lazy"
      :label="$t('exterior.subline')"
      :headline="$t('exterior.headline', { modelName: vehicleStore.stage?.modelName || '...' })"
    >
      <SectionExterior />
      <template #loading><SectionExteriorSkeleton /></template>
    </SPASection>

    <SPASection
      name="interior"
      loading="lazy"
      :label="$t('interior.subline')"
      :headline="$t('interior.headline', { modelName: vehicleStore.stage?.modelName || '...' })"
    >
      <SectionInterior />
      <template #loading><SectionInteriorSkeleton /></template>
    </SPASection>

    <SPASection name="nba" loading="lazy">
      <SectionNba />
      <template #loading><SectionNbaSkeleton /></template>
    </SPASection>

    <SPASection
      name="features"
      loading="lazy"
      :label="$t('features.subline')"
      :headline="$t('features.headline', { modelName: vehicleStore.stage?.modelName || '...' })"
    >
      <SectionFeatures />
      <template #loading><SectionFeaturesSkeleton /></template>
    </SPASection>

    <SPASection
      name="techData"
      loading="lazy"
      :label="$t('techData.subline')"
      :headline="$t('techData.headline', { modelName: vehicleStore.stage?.modelName || '...' })"
    >
      <SectionTechData />
      <template #loading><SectionTechDataSkeleton /></template>
    </SPASection>

    <SPASection
      name="offerTeaser"
      loading="lazy"
      :label="$t('offerTeaser.subline')"
      :headline="$t('offerTeaser.headline', { modelName: vehicleStore.stage?.modelName || '...' })"
    >
      <SectionOfferTeaser />
      <template #loading><SectionOfferTeaserSkeleton /></template>
    </SPASection>
  </main>
</template>

<style lang="scss">
.owpi-spa {
  position: relative;
  // transition: $wb-fade background 0.2s;

  &--theme-dark {
    background-color: var(--wb-black);
    @extend %theme-amg;
  }
}
</style>
